import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const FAQS = [
  {
    category: 'Orders & Accounts',
    items: [
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" in the top navigation. You\'ll need to provide your name, institutional email address, and confirm your research eligibility. Once verified, you can place orders.',
      },
      {
        q: 'Who is eligible to purchase?',
        a: 'Aloha Amino sells exclusively to licensed researchers, postdoctoral fellows, graduate students with lab affiliation, and qualified laboratory providers. Each account is reviewed for research-use eligibility before orders are fulfilled.',
      },
      {
        q: 'How do I place an order?',
        a: 'Browse the catalog, add items to your cart, and proceed through checkout. Payment is accepted by major credit cards via Stripe.',
      },
      {
        q: 'Can I cancel or modify an order after placing it?',
        a: 'Orders can be cancelled within 1 hour of placement if they have not yet been packed. Contact us at support@alohaamino.example.com immediately with your order number. Once packed, cancellations are not possible.',
      },
    ],
  },
  {
    category: 'Shipping & Delivery',
    items: [
      {
        q: 'Where do you ship?',
        a: 'Aloha Amino ships domestically only. We fulfil from our distribution centre. Shipments are made on business days (Monday–Friday, excluding public holidays).',
      },
      {
        q: 'How long does delivery take?',
        a: 'Standard delivery is 3–7 business days. Express (1–3 business days) is available at checkout for an additional fee. Tracking information is emailed when your order ships.',
      },
      {
        q: 'Do you ship internationally?',
        a: 'Not at this time. International regulatory requirements for research chemicals vary significantly, and we currently serve domestic purchasers only.',
      },
    ],
  },
  {
    category: 'Certificates of Analysis',
    items: [
      {
        q: 'What is a Certificate of Analysis (COA)?',
        a: 'A COA is a document from an independent, accredited third-party laboratory that confirms the identity, purity (by HPLC), and mass-spec fingerprint of a specific batch. Every Aloha Amino batch has a COA.',
      },
      {
        q: 'How do I request a COA for my order?',
        a: 'Email support@alohaamino.example.com with your order number and the product batch code (found on the vial label). We\'ll reply with the COA PDF within 1 business day. You can also browse available COAs in the COA Library.',
      },
      {
        q: 'What purity levels do you guarantee?',
        a: 'All peptides are guaranteed ≥ 98% purity by HPLC. Batches that do not meet this threshold are quarantined and not shipped.',
      },
    ],
  },
];

function Accordion({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="divide-y divide-gray-200 border border-gray-200 rounded-xl overflow-hidden">
      {items.map((item, i) => (
        <div key={i}>
          <button
            className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors focus:outline-none focus-visible:bg-gray-50"
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span className="font-semibold text-gray-900 pr-4">{item.q}</span>
            <svg
              className={`w-5 h-5 text-tea-600 shrink-0 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {open === i && (
            <div className="px-6 pb-5 text-gray-600 leading-relaxed text-[15px]">
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="bg-tea-700 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Lab Support</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
            How can we help?
          </h1>
          <p className="mt-4 text-tea-200 text-lg max-w-xl">
            Answers to common questions about orders, COAs, and shipping.
          </p>
        </div>
      </section>

      {/* Contact bar */}
      <div className="bg-tea-50 border-b border-tea-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-tea-800">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            <a href="mailto:support@alohaamino.example.com" className="font-medium hover:underline">
              support@alohaamino.example.com
            </a>
          </div>
          <span className="text-tea-600">Response within 1 business day · Mon–Fri 9 AM–5 PM ET</span>
          <span className="ml-auto">
            <Link to="/coa" className="text-tea-700 font-medium hover:underline">COA Library →</Link>
          </span>
        </div>
      </div>

      {/* FAQ */}
      <main className="flex-1 py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-14">
          {FAQS.map((section) => (
            <div key={section.category}>
              <h2 className="text-xl font-bold text-gray-900 mb-5">{section.category}</h2>
              <Accordion items={section.items} />
            </div>
          ))}

          {/* Still have questions */}
          <div className="bg-tea-50 rounded-2xl p-8 text-center">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Still have questions?</h3>
            <p className="text-gray-600 mb-5">
              Our lab support team typically responds within one business day.
            </p>
            <a
              href="mailto:support@alohaamino.example.com"
              className="btn-primary"
            >
              Email Lab Support
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
